import React, { useEffect, useMemo, useState } from "react";
import { View, Text, StyleSheet, TouchableOpacity } from "react-native";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import { ProductFieldInfoVisibility } from "../../../../../view-models/ProductFieldInfoVisibility";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";
import identificationsData from "../../../../../constants/identifications.json";
import { loadCountries } from "../../../../../services/common.service";

type Props = {
  onValidationChange?: (isValid: boolean) => void;
};

const AdditionalInformationStep: React.FC<Props> = ({ onValidationChange }) => {
  const missingRequiredFields = useSessionStore((s: SessionState) => (s as any).missingRequiredFields) as any[];
  const setAdditionalFieldValue = useSessionStore((s: SessionState) => (s as any).setAdditionalFieldValue);
  const additionalFieldValues = useSessionStore((s: SessionState) => (s as any).additionalFieldValues) as Record<string, any>;

  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  const [idTypes, setIdTypes] = useState<Array<{ key: string; EnglishLabel: string; SpanishLabel: string; Value?: string; photoIdCountry?: string; photoIdState?: string }>>([]);
  const [showIdTypeOptions, setShowIdTypeOptions] = useState<boolean>(false);
  const [showStateOptions, setShowStateOptions] = useState<boolean>(false);
  const [availableStates, setAvailableStates] = useState<Array<{ stateid: number; stateAbbrivation: string; stateName: string }>>([]);

  const requiredFields = useMemo(() => (missingRequiredFields || []).filter((f: any) => (f?.visibility === ProductFieldInfoVisibility.REQ || f?.Visibility === ProductFieldInfoVisibility.REQ || f?.Visibility === 0)), [missingRequiredFields]);
  const isIdTypeRequired = useMemo(() => {
    return (missingRequiredFields || []).some((f: any) => {
      const k = String(f?.XmlTag ?? f?.xmlTag ?? "");
      return k === 'senderPhotoIdType';
    });
  }, [missingRequiredFields]);

  // If senderLegalIdType is required, auto-set to SSN
  useEffect(() => {
    const hasSenderLegalIdType = (missingRequiredFields || []).some((f: any) => String(f?.XmlTag ?? f?.xmlTag ?? "") === 'senderLegalIdType');
    if (hasSenderLegalIdType) {
      try { setAdditionalFieldValue?.('senderLegalIdType', 'SSN'); } catch {}
    }
  }, [missingRequiredFields]);

  useEffect(() => {
    const errs: Record<string, string> = {};
    for (const f of requiredFields) {
      const key = String(f?.XmlTag ?? f?.xmlTag ?? "");
      let label = String(f?.FieldLabel ?? f?.fieldLabel ?? key);
      if(label.length === 0 || label.trim() === '') {
        label = key;
      }
      const min = Number(f?.FieldMin ?? f?.fieldMin ?? 0);
      const max = Number(f?.FieldMax ?? f?.fieldMax ?? Number.MAX_SAFE_INTEGER);
      const valueRaw = additionalFieldValues?.[key] ?? "";
      const value = String(valueRaw ?? "");
      if (value.length < min) errs[key] = `${label} must be at least ${min} characters`;
      else if (value.length > max) errs[key] = `${label} must be at most ${max} characters`;
      else if (!value) errs[key] = `${label} is required`;
    }
    setLocalErrors(errs);
    onValidationChange?.(Object.keys(errs).length === 0);
  }, [requiredFields, additionalFieldValues]);

  useEffect(() => {
    const raw: any = identificationsData as any;
    let list: Array<{ key: string; EnglishLabel: string; SpanishLabel: string; Value?: string; photoIdCountry?: string; photoIdState?: string }> = [];
    if (Array.isArray(raw?.IDTypes)) {
      list = (raw.IDTypes as any[]).map((x: any) => ({
        key: x.key ?? x.IDkey ?? x.Value,
        EnglishLabel: x.EnglishLabel,
        SpanishLabel: x.SpanishLabel,
        Value: x.Value,
        photoIdCountry: x.photoIdCountry,
        photoIdState: x.photoIdState,
      }));
    } else if (raw && typeof raw === "object") {
      list = Object.entries(raw).map(([key, v]: [string, any]) => ({
        key,
        EnglishLabel: v?.EnglishLabel,
        SpanishLabel: v?.SpanishLabel,
        Value: v?.Value,
        photoIdCountry: v?.photoIdCountry,
        photoIdState: v?.photoIdState,
      }));
    }
    setIdTypes(list.filter(x => x && x.key));
  }, []);

  // Derive selected ID Type object from currently selected Value (senderPhotoIdType)
  const selectedIdType = useMemo(() => {
    const selectedValue = String(additionalFieldValues?.['senderPhotoIdType'] ?? "");
    if (!selectedValue) return null;
    return idTypes.find((t) => String(t.Value ?? "") === selectedValue) ?? null;
  }, [idTypes, additionalFieldValues]);

  // When ID type changes, auto-set photoIdCountry and handle state logic
  useEffect(() => {
    const country = String(selectedIdType?.photoIdCountry ?? "");
    if (country) {
      try { setAdditionalFieldValue?.('senderPhotoIdCountry', country); } catch {}
    }

    const handleStates = async () => {
      if (country === 'MEX') {
        // Auto-set default state from definition and do not show the state field
        const defaultState = String(selectedIdType?.photoIdState ?? 'AG');
        try { setAdditionalFieldValue?.('senderPhotoIdState', defaultState); } catch {}
        setAvailableStates([]);
        setShowStateOptions(false);
        return;
      }
      if (country === 'USA' || country === 'CAN') {
        try {
          const result: any = await loadCountries();
          const list: any[] = (result?.data ?? []) as any[];
          const countryObj = list.find((c: any) => c?.CountryCode === country);
          const states: Array<{ stateid: number; stateAbbrivation: string; stateName: string }> = Array.isArray(countryObj?.States) ? countryObj.States : [];
          setAvailableStates(states);
          return;
        } catch (e) {
          console.error('Failed to load states for', country, e);
          setAvailableStates([]);
        }
      }
      setAvailableStates([]);
    };

    handleStates();
  }, [selectedIdType]);

  const handleChange = (key: string, text: string) => {
    setAdditionalFieldValue?.(key, text);
  };

  return (
    <View style={styles.container}>
      <Text style={[styles.title, {paddingRight: 100}]}>We need a few more details</Text>
      {requiredFields.map((f: any) => {
        const key = String(f?.XmlTag ?? f?.xmlTag ?? "");
        const label = String(f?.FieldLabel ?? f?.fieldLabel ?? key);
        const placeholder = label;
        const value = String((additionalFieldValues?.[key] ?? ""));
        const error = localErrors[key];
        // Country is derived from the selected ID type; do not render this field even if required
        if (key === 'senderPhotoIdCountry') {
          return null;
        }
        if (key === 'senderPhotoIdType') {
          const selectedOption = idTypes.find((x) => String(x.Value ?? "") === value);
          const displayText = selectedOption ? selectedOption.EnglishLabel : placeholder;
          return (
            <View key={key} style={styles.fieldWrapper}>
              <Text style={styles.label}>{label} {key}</Text>
              <View style={styles.dropdownContainer}>
                <TouchableOpacity
                  style={styles.dropdownSelected}
                  onPress={() => setShowIdTypeOptions((prev) => !prev)}
                >
                  <Text style={styles.dropdownSelectedText}>{displayText}</Text>
                </TouchableOpacity>
                {showIdTypeOptions && (
                  <View style={styles.dropdownList}>
                    {idTypes.map((opt) => (
                      <TouchableOpacity
                        key={opt.key}
                        style={styles.dropdownItem}
                        onPress={() => {
                          setAdditionalFieldValue?.(key, String(opt.Value ?? ""));
                          // Also set derived fields
                          if (opt.photoIdCountry) {
                            setAdditionalFieldValue?.('senderPhotoIdCountry', String(opt.photoIdCountry));
                          }
                          if (opt.photoIdCountry === 'MEX') {
                            const defaultState = String(opt.photoIdState ?? 'AG');
                            setAdditionalFieldValue?.('senderPhotoIdState', defaultState);
                          }
                          setShowIdTypeOptions(false);
                        }}
                      >
                        <Text style={styles.dropdownItemText}>{opt.EnglishLabel}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              </View>
              {!!error && <Text style={styles.error}>{error}</Text>}
            </View>
          );
        }
        if (key === 'senderPhotoIdState') {
          const country = String(selectedIdType?.photoIdCountry ?? additionalFieldValues?.['senderPhotoIdCountry'] ?? "");
          if (country === 'MEX') {
            // Hide input, auto-set value handled in effect/selection
            return null;
          }
          if (country === 'USA' || country === 'CAN') {
            const selectedState = availableStates.find((s) => String(s.stateAbbrivation) === value);
            const displayStateText = selectedState ? selectedState.stateName : placeholder;
            return (
              <View key={key} style={styles.fieldWrapper}>
                <Text style={styles.label}>{label} {key}</Text>
                <View style={styles.dropdownContainer}>
                  <TouchableOpacity
                    style={styles.dropdownSelected}
                    onPress={() => setShowStateOptions((prev) => !prev)}
                  >
                    <Text style={styles.dropdownSelectedText}>{displayStateText}</Text>
                  </TouchableOpacity>
                  {showStateOptions && (
                    <View style={styles.dropdownList}>
                      {availableStates.map((opt) => (
                        <TouchableOpacity
                          key={opt.stateAbbrivation}
                          style={styles.dropdownItem}
                          onPress={() => {
                            setAdditionalFieldValue?.(key, String(opt.stateAbbrivation));
                            setShowStateOptions(false);
                          }}
                        >
                          <Text style={styles.dropdownItemText}>{opt.stateName}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  )}
                </View>
                {!!error && <Text style={styles.error}>{error}</Text>}
              </View>
            );
          }
          // For other countries, do not display the state field
          return null;
        }
        // Render senderLegalIdType as read-only when present and keep its value as SSN
        if (key === 'senderLegalIdType') {
          const fixedValue = 'SSN';
          return (
            <View key={key} style={styles.fieldWrapper}>
              <Text style={styles.label}>{label} {key}</Text>
              <FloatingTextInputComponent
                testID={`billPayAdditionalField_${key}`}
                label={placeholder}
                value={fixedValue}
                onChangeText={() => {}}
                editable={false}
              />
              {!!error && <Text style={styles.error}>{error}</Text>}
            </View>
          );
        }
        return (
          <View key={key} style={styles.fieldWrapper}>
            <Text style={styles.label}>{label} {key}</Text>
            <FloatingTextInputComponent
              testID={`billPayAdditionalField_${key}`}
              label={placeholder}
              value={value}
              onChangeText={(t) => handleChange(key, t)}
            />
            {!!error && <Text style={styles.error}>{error}</Text>}
          </View>
        );
      })}
      {/* Conditionally render state when ID type is asked and country is USA/CAN, even if not in missingRequiredFields */}
      {(() => {
        const country = String(selectedIdType?.photoIdCountry ?? additionalFieldValues?.['senderPhotoIdCountry'] ?? "");
        if (!isIdTypeRequired) return null;
        if (country !== 'USA' && country !== 'CAN') return null;
        const key = 'senderPhotoIdState';
        const value = String((additionalFieldValues?.[key] ?? ""));
        const selectedState = availableStates.find((s) => String(s.stateAbbrivation) === value);
        const displayStateText = selectedState ? selectedState.stateName : 'Select state of ID issue';
        return (
          <View key={key + '-extra'} style={styles.fieldWrapper}>
            <Text style={styles.label}>State of ID issue {key}</Text>
            <View style={styles.dropdownContainer}>
              <TouchableOpacity
                style={styles.dropdownSelected}
                onPress={() => setShowStateOptions((prev) => !prev)}
              >
                <Text style={styles.dropdownSelectedText}>{displayStateText}</Text>
              </TouchableOpacity>
              {showStateOptions && (
                <View style={styles.dropdownList}>
                  {availableStates.map((opt) => (
                    <TouchableOpacity
                      key={opt.stateAbbrivation}
                      style={styles.dropdownItem}
                      onPress={() => {
                        setAdditionalFieldValue?.(key, String(opt.stateAbbrivation));
                        setShowStateOptions(false);
                      }}
                    >
                      <Text style={styles.dropdownItemText}>{opt.stateName}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </View>
          </View>
        );
      })()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { paddingHorizontal: 24, marginTop: 16, backgroundColor: Colors.white },
  title: { fontSize: 20, fontWeight: "700", marginBottom: 8 },
  subtitle: { fontSize: 14, color: "#555" },
  fieldWrapper: { marginBottom: 16 },
  label: { fontSize: 14, color: "#111", marginBottom: 6 },
  error: { color: "#E11D48", marginTop: 6, fontSize: 12 },
  dropdownContainer: { position: 'relative' },
  dropdownSelected: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 12,
    backgroundColor: '#FFFFFF',
  },
  dropdownSelectedText: { fontSize: 14, color: '#111' },
  dropdownList: {
    marginTop: 8,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    overflow: 'hidden'
  },
  dropdownItem: { paddingVertical: 10, paddingHorizontal: 12 },
  dropdownItemText: { fontSize: 14, color: '#111' }
});

export default AdditionalInformationStep;

